import { appendTrackPoint } from "@/lib/tracksDb"
import type { TrackRecorderService } from "../types"

type WebRecording = {
  trackId: string
  watchId: number
  cancelled: boolean
  appendQueue: Promise<void>
  onPointRecorded?: (trackId: string) => void
}

let activeRecording: WebRecording | undefined
let lifecycleQueue: Promise<void> = Promise.resolve()

const enqueueLifecycle = (operation: () => Promise<void>) => {
  const next = lifecycleQueue.then(operation)
  lifecycleQueue = next.catch(() => {})
  return next
}

const stopActiveRecording = async () => {
  const recording = activeRecording
  if (!recording) return

  // Cancel first. A callback already delivered by the browser may be queued
  // behind an append, but it must not append after this stop completes.
  recording.cancelled = true
  navigator.geolocation.clearWatch(recording.watchId)
  if (activeRecording === recording) activeRecording = undefined
  await recording.appendQueue
}

const enqueueLocation = (
  recording: WebRecording,
  location: {
    latitude: number
    longitude: number
    speed?: number | null
    timestamp: number
  },
) => {
  const next = recording.appendQueue.then(async () => {
    if (recording.cancelled) return
    await appendTrackPoint(
      recording.trackId,
      [location.longitude, location.latitude],
      location.timestamp,
      location.speed ?? null,
    )
    try {
      recording.onPointRecorded?.(recording.trackId)
    } catch {
      // A cache refresh failure must not turn a durable append into a failed
      // capture or block later locations in the serialized queue.
      console.warn("Unable to refresh recorded track points")
    }
  })
  // Keep the queue alive after one failed fix so later browser callbacks and
  // stopAndFlush can still settle deterministically.
  recording.appendQueue = next.catch(() => {
    console.warn("Unable to record track location")
  })
}

export const trackRecorder: TrackRecorderService = {
  async startRecording({ trackId, onPointRecorded }) {
    if (!navigator.geolocation) {
      console.warn("Track recording is unavailable without geolocation support")
      return
    }

    await enqueueLifecycle(async () => {
      if (!navigator.geolocation) return
      if (activeRecording?.trackId === trackId) return

      await stopActiveRecording()

      const recording: WebRecording = {
        trackId,
        watchId: -1,
        cancelled: false,
        appendQueue: Promise.resolve(),
        onPointRecorded,
      }
      activeRecording = recording

      try {
        recording.watchId = navigator.geolocation.watchPosition(
          (position) => {
            enqueueLocation(recording, {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              speed: position.coords.speed,
              timestamp: position.timestamp,
            })
          },
          () => {
            console.warn("Track recording location error")
          },
          { enableHighAccuracy: true },
        )
      } catch (error) {
        recording.cancelled = true
        if (activeRecording === recording) activeRecording = undefined
        throw error
      }
    })
  },

  async stopRecording() {
    await enqueueLifecycle(stopActiveRecording)
  },

  async stopAndFlush() {
    // There is no native queue on web; stopping waits for the serialized raw
    // append queue, which is the complete flush operation.
    await trackRecorder.stopRecording()
  },
}
